import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { cx } from "./ui";

/** Transient confirmation, mirroring the desktop's Toast component. */

type ToastTone = "success" | "error" | "info";

interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
}

interface ToastApi {
  show: (message: string, tone?: ToastTone) => void;
  success: (message: string) => void;
  error: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const DURATION_MS = 4000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const show = useCallback((message: string, tone: ToastTone = "info") => {
    const id = Date.now() + Math.random();
    setItems((current) => [...current, { id, message, tone }]);
    window.setTimeout(() => {
      setItems((current) => current.filter((item) => item.id !== id));
    }, DURATION_MS);
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (message: string) => show(message, "success"),
      error: (message: string) => show(message, "error"),
    }),
    [show],
  );

  const tones: Record<ToastTone, string> = {
    success: "border-success/40 bg-surface text-success",
    error: "border-danger/40 bg-surface text-danger",
    info: "border-line bg-surface text-fg",
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* Bottom-centre on phones (thumb reach, clear of the nav bar), bottom-right on desktop. */}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-3 bottom-4 z-50 flex flex-col items-center gap-2 sm:inset-x-auto sm:right-5 sm:items-end"
      >
        {items.map((item) => (
          <div
            key={item.id}
            className={cx(
              "pointer-events-auto max-w-md rounded-lg border px-4 py-2.5 text-sm shadow-lg",
              tones[item.tone],
            )}
          >
            {item.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used inside <ToastProvider>");
  return context;
}
